import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RootState, StoreDispatch } from "../../store/configure-store"
import { useQuery as reactUseQuery } from "@tanstack/react-query"
import { FromDropdown } from "../../cmps/forms/input/form-dropdown"
import { SystemTypes, isRequiredSystem } from '../../cmps/forms/is-required-system'
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { AddEntityModal, EntityDetails } from "../../cmps/forms/modals/add-entity-modal"
import { AddEntitySystemModal, EntitySystemDetails } from "../../cmps/forms/modals/add-entity-system-modal"
import { AddAuditYearModal, AuditYearDetails } from "../../cmps/forms/modals/add-audit-year-modal"
import { httpService } from "../../services/http-service"
import { resolveMasterCompanyId } from "../../services/resolve-master-company-id"
import { createEntitySystemMutation } from "../../cmps/util-cmps/graphql-mutations/create-entity-system"
import { createAuditMutation } from "../../cmps/util-cmps/graphql-mutations/create-audit"
import { createEntityMutation } from "../../cmps/util-cmps/graphql-mutations/create-entity"
import { getControlByObjectiveAndSystemsQuery } from "../../cmps/util-cmps/graphql-queries/control-by-objective-and-systems"
import { ExistingControl, ExistingControlSection } from "../../cmps/forms/existing-control-section"
import { createAuditControlMutation } from "../../cmps/util-cmps/graphql-mutations/create-audit-control"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useDraggable } from "../../hooks/use-draggable"
import { ConsolidationConfigBuilder, validateConsolidationPairings } from "../../cmps/audit-control/consolidation-config-builder"

interface PageDetailsRes {
    companies: {
        id: number
        name: string
        entity: {
            id: number
            name: string
            entitySystems: {
                id: number
                system: {
                    id: number
                    name: string
                }
            }[]
            audit: {
                id: number
                year: number
            }[]
        }[]
        user: {
            id: string
            email: string
            firstName: string
            lastName: string
        }[]
    }[]
    objectives: {
        id: number
        name: string
        objectiveNumber: number
        domainAbbreviation: string
    }[]
}

interface SelectedDetails {
    entityId: number | null
    objectiveId: number | null
    mainSystemId: number | null
    hrSystemId: number | null
    ticketSystemId: number | null
    overwriteControlId: number | null
    consolidationJson: string | null
    auditId: number | null
    controlOwnerId: string | null
    companyControlId: string | null
}

const pageDetailsQuery = (companyName: string) => `
{
  companies(name: "${companyName}") {
    id
    name
    entity(sorting: ASC) {
      id
      name
      entitySystems(sorting: ASC) {
        id
        system {
          id
          name
        }
      }
      audit(sorting: DESC) {
        id
        year
        auditor {
            id
            name
        }
      }
    }
    user {
      id
      email
      firstName
      lastName
    }
  }
  objectives(sorting: ASC) {
    id
    name
    objectiveNumber
    domainAbbreviation
  }
}
`

const getEmptySelectedDetails = () => ({
    entityId: null,
    objectiveId: null,
    mainSystemId: null,
    hrSystemId: null,
    ticketSystemId: null,
    overwriteControlId: null,
    consolidationJson: null,
    auditId: null,
    controlOwnerId: null,
    companyControlId: null,
})

interface AddAuditControlFormPageProps {
    onClose: () => void
    onSuccess?: () => void
    cloneFrom?: Partial<SelectedDetails> & { sourceAcId?: number }
}

export const AddAuditControlFormPage: React.FC<AddAuditControlFormPageProps> = ({ onClose, onSuccess, cloneFrom }) => {
    const dispatch = useDispatch<StoreDispatch>()
    const companyName: any = useSelector((stateModule: RootState) => stateModule.companyName)!
    const [pageDetails, setPageDetails] = useState<PageDetailsRes>()
    const [selectedDetails, setSelectedDetails] = useState<SelectedDetails>(() => {
        const empty = getEmptySelectedDetails()
        return cloneFrom ? { ...empty, ...cloneFrom } : empty
    })
    const [existingControl, setExistingControl] = useState<ExistingControl>({ status: "new", controlId: null })
    const [isAddEntityModalOpen, setIsAddEntityModalOpen] = useState(false)
    const [openedEntitySystemModal, setOpenedEntitySystemModal] = useState<SystemTypes | null>(null)
    const [isAddAuditYearModalOpen, setIsAddAuditYearModalOpen] = useState(false)
    const [isAddingAuditControl, setIsAddingAuditControl] = useState(false)
    // When entity changes, we try to re-map each system slot to the new entity's
    // entity_system by system NAME. Names that don't exist on the new entity show up
    // in this list and are surfaced as a banner under the Entity dropdown.
    const [missingSystemsForEntity, setMissingSystemsForEntity] = useState<string[]>([])

    const company = pageDetails?.companies?.[0] ?? null

    useEscapeKey(onClose)
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    const fetchPageDetails = async () => {
        const query = pageDetailsQuery(companyName.value)
        const pageDetails: PageDetailsRes = await httpService.graphql(companyName.value, query)

        setPageDetails(pageDetails)
        return pageDetails
    }

    const { isLoading, isFetching, isError, refetch: refetchPageDetails } = reactUseQuery({
        queryKey: ['page-details', companyName.value],
        queryFn: fetchPageDetails,
        retry: 1,
    })

    const fetchExistingControl = async () => {
        setExistingControl({ status: "loading", controlId: null })

        try {
            const query = getControlByObjectiveAndSystemsQuery()
            const variables = {
                objectiveId: selectedDetails.objectiveId,
                mainSystemId: selectedDetails.mainSystemId,
                hrSystemId: selectedDetails.hrSystemId,
                ticketSystemId: selectedDetails.ticketSystemId
            }
            const reqBody = { query, variables }
            const res = await httpService.graphql(companyName.value, reqBody)

            if (res.controlByObjectiveAndSystems === null) {
                setExistingControl({ status: "fetched", controlId: null })
                return
            }

            const controlId = res.controlByObjectiveAndSystems.id
            const rawConsolidation = res.controlByObjectiveAndSystems.systemConsolidations
            const consolidationStr = rawConsolidation
                ? (typeof rawConsolidation === 'string' ? rawConsolidation : JSON.stringify(rawConsolidation))
                : null
            setExistingControl({ status: "fetched", controlId, consolidationJson: consolidationStr })
            // Default to "Use existing control" — but when cloning, keep the cloned
            // consolidation config rather than overwriting with the existing control's config.
            setSelectedDetails(prev => ({
                ...prev,
                overwriteControlId: controlId,
                ...(!cloneFrom ? { consolidationJson: consolidationStr ?? prev.consolidationJson } : {}),
            }))
        } catch {
            const msg: UserMsg = { type: 'danger', txt: 'Failed checking whether control exists' }
            dispatch(setUserMsg({ msg }))
        }
    }

    useEffect(() => {
        setExistingControl({ status: "new", controlId: null })
        setSelectedDetails(prevState => ({ ...prevState, overwriteControlId: null }))

        const { entityId, objectiveId, mainSystemId, hrSystemId } = selectedDetails
        if (!entityId || !objectiveId ||
            (isRequiredSystem(getObjectiveNum(), SystemTypes.IN_SCOPE) && !mainSystemId) ||
            (isRequiredSystem(getObjectiveNum(), SystemTypes.HR) && !hrSystemId)
        ) {
            return
        }

        // Existing-control lookup runs for every (entityId, objectiveId, systems) combo,
        // clone or not. mainSystemId/hrSystemId/ticketSystemId in selectedDetails are
        // already re-mapped to the new entity's entity_systems when the entity changes
        // (see onChangeFormValue), so the query naturally finds the right Control for
        // the new entity. Behaviour at submit:
        //   - match found → user can pick Use existing (attach to that Control) or
        //     Create new. "Use existing" updates that Control's consolidations and
        //     company_control_id with the submitted values — see
        //     CreateAuditControl.mutate.
        //   - no match → fresh Control row inserted.
        fetchExistingControl()

    }, [selectedDetails.entityId, selectedDetails.objectiveId, selectedDetails.mainSystemId, selectedDetails.hrSystemId, selectedDetails.ticketSystemId])

    const companyInitialized = useRef(false)
    useEffect(() => {
        if (companyInitialized.current) {
            // Only reset on company CHANGE, not on initial mount (preserves cloneFrom data)
            setSelectedDetails(getEmptySelectedDetails())
        }
        companyInitialized.current = true
        refetchPageDetails()
    }, [companyName.value])

    const onChangeFormValue = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const { name, value } = event.target

        let updatedValue: null | string | number = value
        if (value === '') updatedValue = null
        else if (!['consolidationJson', 'controlOwner', 'itSupport'].includes(name)) updatedValue = +value

        if (name === 'entity') {
            // mainSystemId / hrSystemId / ticketSystemId are entity_system.id values tied
            // to the previous entity, so they're invalid against the new entity. Resolve
            // each by system NAME: find the new entity's entity_system whose system.name
            // matches the old one. If a match exists, keep the slot selected (using the
            // new entity_system.id). If not, blank it and report the missing system in a
            // banner below the Entity dropdown.
            const newEntityId = updatedValue as number
            const newEntity = company?.entity.find((e: any) => e.id === newEntityId)
            const prevEntity = company?.entity.find((e: any) => e.id === selectedDetails.entityId)
            const currentYearAudit = newEntity?.audit?.find((a: any) => a.year === new Date().getFullYear())

            const remapSystem = (oldEntitySystemId: number | null, label: string): { id: number | null; missing: string | null } => {
                if (oldEntitySystemId == null) return { id: null, missing: null }
                const oldEs = prevEntity?.entitySystems?.find((es: any) => es.id === oldEntitySystemId)
                const systemName: string | undefined = oldEs?.system?.name
                if (!systemName) return { id: null, missing: null }
                const newEs = newEntity?.entitySystems?.find((es: any) => es.system?.name === systemName)
                return newEs ? { id: newEs.id, missing: null } : { id: null, missing: `${label} (${systemName})` }
            }

            const main = remapSystem(selectedDetails.mainSystemId, 'Main')
            const hr = remapSystem(selectedDetails.hrSystemId, 'HR')
            const ticket = remapSystem(selectedDetails.ticketSystemId, 'Ticket')

            setMissingSystemsForEntity(
                [main.missing, hr.missing, ticket.missing].filter((m): m is string => !!m)
            )

            setSelectedDetails(prev => ({
                ...prev,
                entityId: newEntityId,
                auditId: currentYearAudit?.id ?? null,
                mainSystemId: main.id,
                hrSystemId: hr.id,
                ticketSystemId: ticket.id,
                overwriteControlId: null,
            }))
            return
        }

        const key = name === 'consolidationJson' ? name : `${name}Id`
        setSelectedDetails({ ...selectedDetails, [key]: updatedValue })
    }

    const onExistingControlOverride = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedOption = event.target.id;
        if (selectedOption === 'override-yes') {
            setSelectedDetails({
                ...selectedDetails,
                overwriteControlId: existingControl.controlId,
                consolidationJson: existingControl.consolidationJson || selectedDetails.consolidationJson,
            })
            return
        }
        setSelectedDetails({ ...selectedDetails, overwriteControlId: 0 })
    }

    const getSelectedEntity = () => {
        const selectedEntityId = selectedDetails.entityId
        if (!selectedEntityId || !company) return null

        return company.entity.find((entity) => entity.id === selectedEntityId)
    }

    const getEntitySystemList = () => {
        const selectedEntity = getSelectedEntity()
        if (!selectedEntity) return []

        return selectedEntity.entitySystems.map(
            (entitySystem) => ({ id: entitySystem.id, name: entitySystem.system.name })
        )
    }

    const getObjectiveNum = () => {
        const selectedObjectiveId = selectedDetails.objectiveId
        if (!selectedObjectiveId || !pageDetails?.objectives) return null

        return pageDetails.objectives.find((objective) => objective.id === selectedObjectiveId)?.objectiveNumber ?? null
    }

    /** Returns system types for consolidation builder based on actually selected systems */
    const getSelectedSystemTypes = () => {
        const types: string[] = []
        if (selectedDetails.mainSystemId) types.push(SystemTypes.IN_SCOPE)
        if (selectedDetails.hrSystemId) types.push(SystemTypes.HR)
        if (selectedDetails.ticketSystemId) types.push(SystemTypes.TICKET)
        return types.length > 0 ? types : [SystemTypes.IN_SCOPE]
    }

    const onConsolidationChange = (val: string) => {
        setSelectedDetails(prev => ({ ...prev, consolidationJson: val }))
    }

    const getAuditYearList = () => {
        const selectedEntity = getSelectedEntity()
        if (!selectedEntity) return []

        return selectedEntity.audit.map(
            (audit) => ({ id: audit.id, name: audit.year.toString() })
        )
    }

    const getUsersList = () => {
        if (!company) return []
        return company.user.map((user) => ({ id: user.id, name: `${user.firstName} ${user.lastName}` }))
    }

    const onAddEntity = async (entityDetails: EntityDetails) => {
        if (!company) return
        const { name, abbreviation, domains } = entityDetails
        // CreateEntity writes to master DB — must use master's company id, not silo's local id.
        const masterCompanyId = await resolveMasterCompanyId(companyName.value, company.id)
        const query = createEntityMutation(masterCompanyId, name, abbreviation, domains)
        const res = await httpService.graphql(companyName.value, query)
        if (res?.errors?.length) throw new Error(res.errors[0]?.message || 'Create failed')
        const newEntity = { ...res.createEntity.entity, entitySystems: [], audit: [] }

        const updatedEntities = [...company.entity, newEntity].sort((a, b) => a.name.localeCompare(b.name))
        const updatedCompany = { ...company, entity: updatedEntities }

        const updatedPageDetails = {
            ...pageDetails,
            companies: [updatedCompany]
        }
        setPageDetails(updatedPageDetails as PageDetailsRes)
        setSelectedDetails({ ...selectedDetails, entityId: res.createEntity.entity.id })
        setIsAddEntityModalOpen(false)
    }

    const onAddEntitySystem = async (systemType: SystemTypes, entitySystemDetails: EntitySystemDetails) => {
        if (!company) return
        const { systemId, integrationType, isRequiredDaily } = entitySystemDetails
        const query = createEntitySystemMutation(selectedDetails.entityId!, systemId!, integrationType, isRequiredDaily)
        const res = await httpService.graphql(companyName.value, query)
        const newEntitySystem = res.createEntitySystem.entitySystem

        const selectedEntity = getSelectedEntity()
        const updatedEntitySystems = [...selectedEntity!.entitySystems, newEntitySystem].sort((a, b) => a.system.name.localeCompare(b.system.name))
        const updatedEntity = { ...selectedEntity, entitySystems: updatedEntitySystems }
        const updatedEntities = company.entity.map((entity) => entity.id === selectedDetails.entityId ? updatedEntity : entity)
        const updatedCompany = { ...company, entity: updatedEntities }

        const updatedPageDetails = {
            ...pageDetails,
            companies: [updatedCompany]
        }
        setPageDetails(updatedPageDetails as PageDetailsRes)
        setSelectedDetails({ ...selectedDetails, [`${systemType}SystemId`]: newEntitySystem.id })
        setOpenedEntitySystemModal(null)
    }

    const onAddAuditYear = async (auditYearDetails: AuditYearDetails) => {
        if (!company) return
        const { year, auditorId } = auditYearDetails
        const query = createAuditMutation(selectedDetails.entityId!, year, auditorId!)
        const res = await httpService.graphql(companyName.value, query)
        const { auditor, ...newAudit } = res.createAudit.audit

        const selectedEntity = getSelectedEntity()
        const updatedAudit = [...selectedEntity!.audit, newAudit].sort((a, b) => b.year - a.year)
        const updatedEntity = { ...selectedEntity, audit: updatedAudit }
        const updatedEntities = company.entity.map((entity) => entity.id === selectedDetails.entityId ? updatedEntity : entity)
        const updatedCompany = { ...company, entity: updatedEntities }

        const updatedPageDetails = {
            ...pageDetails,
            companies: [updatedCompany]
        }
        setPageDetails(updatedPageDetails as PageDetailsRes)
        setSelectedDetails({ ...selectedDetails, auditId: newAudit.id })
        setIsAddAuditYearModalOpen(false)
    }

    const onAddAuditControl = async () => {
        if (existingControl.status === 'loading') return

        const { entityId, objectiveId, mainSystemId, hrSystemId, ticketSystemId, consolidationJson, auditId: auditYearId, controlOwnerId } = selectedDetails

        if (!entityId) return triggerErrorMsg('Entity is required')
        if (!objectiveId) return triggerErrorMsg('Objective is required')
        if (isRequiredSystem(getObjectiveNum(), SystemTypes.IN_SCOPE) && !mainSystemId) return triggerErrorMsg('Main System is required')
        if (isRequiredSystem(getObjectiveNum(), SystemTypes.HR) && !hrSystemId) return triggerErrorMsg('HR System is required')
        // Ticket system is never blocking — missing it just makes the AC "partial"
        const hasHrSelected = !!hrSystemId
        const hasTicketSelected = !!ticketSystemId
        const hasMultipleSystems = hasHrSelected || hasTicketSelected

        if (hasMultipleSystems) {
            if (!consolidationJson || consolidationJson === '[]') return triggerErrorMsg('Consolidation config is required')
            const pairingError = validateConsolidationPairings(consolidationJson, hasHrSelected, hasTicketSelected)
            if (pairingError) {
                const msg: UserMsg = { type: 'danger', txt: pairingError }
                dispatch(setUserMsg({ msg }))
                return
            }
        }

        if (!auditYearId) return triggerErrorMsg('Audit Year is required')

        // When cloning, ensure at least one setting differs from the source
        if (cloneFrom) {
            const same = objectiveId === cloneFrom.objectiveId
                && mainSystemId === cloneFrom.mainSystemId
                && hrSystemId === (cloneFrom.hrSystemId ?? null)
                && ticketSystemId === (cloneFrom.ticketSystemId ?? null)
                && entityId === cloneFrom.entityId
                && auditYearId === (cloneFrom.auditId ?? null)
            if (same) return triggerErrorMsg('Clone must differ from the source in at least one setting (e.g., objective, entity, or audit year)')
        }
        if (!controlOwnerId) return triggerErrorMsg('Control Owner is required')
        if (existingControl.status === 'fetched' && existingControl.controlId && selectedDetails.overwriteControlId === null) {
            return triggerErrorMsg('Please select whether to overwrite or create new control')
        }

        setIsAddingAuditControl(true)
        try {
            const query = createAuditControlMutation()
            const variables = {
                companyId: company!.id,
                ...selectedDetails,
            }
            const reqBody = { query, variables }
            const res = await httpService.graphql(companyName.value, reqBody)

            const auditControl = res?.createAuditControl?.auditControl
            if (!auditControl) {
                const msg: UserMsg = { type: 'danger', txt: 'Failed to create audit control. An audit control with this exact configuration may already exist.' }
                dispatch(setUserMsg({ msg }))
                return
            }
            setExistingControl({ status: "fetched", controlId: auditControl.control.id })
            const msg: UserMsg = { type: 'success', txt: `Audit control created successfully (${auditControl.id})` }
            dispatch(setUserMsg({ msg }))
            onSuccess?.()
        } catch (err: any) {
            const serverError = err?.errors?.[0]?.message || err?.error || ''
            const msg: UserMsg = { type: 'danger', txt: serverError || 'Failed creating audit control' }
            dispatch(setUserMsg({ msg }))
        }
        finally {
            setIsAddingAuditControl(false)
        }

    }

    const triggerMissingRequiredFields = () => {
        triggerErrorMsg('Missing Required Fields')
    }

    const triggerErrorMsg = (txt: string) => {
        const msg: UserMsg = { type: 'danger', txt }
        dispatch(setUserMsg({ msg }))
    }

    const renderBody = () => {
        if (isError) return <div className="modal-form-body"><p>Error loading form data.</p></div>
        if (isLoading || isFetching) return <div className="modal-form-body"><Spinner isSmall={true} /></div>
        if (!company) return <div className="modal-form-body"><p>No company data found.</p></div>

        return <section className="modal-form-body">
            <div className="form-section-card">
                <div className="form-section-label">Entity & Objective</div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'entity'}
                        label="Entity"
                        options={company.entity}
                        value={selectedDetails.entityId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={() => setIsAddEntityModalOpen(true)}
                    />
                    {missingSystemsForEntity.length > 0 && (
                        <div className="field-hint field-hint--warn" style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#b54708' }}>
                            The following systems aren't configured for this entity and were cleared:&nbsp;
                            <strong>{missingSystemsForEntity.join(', ')}</strong>.
                            Add them to the entity (or pick different systems) before saving.
                        </div>
                    )}
                </div>
                <div className="field">
                    <FromDropdown
                        keyName={'objective'}
                        label="Objective"
                        options={pageDetails!.objectives}
                        value={selectedDetails.objectiveId || ''}
                        onSelectValue={onChangeFormValue}
                    />
                </div>
                <div className="field field-extra-top">
                    <label htmlFor="companyControlId">
                        Company Control ID
                        <span className="field-info-icon" data-tip="Optional. For companies that require an external reference ID for audit controls (e.g., ZIM uses this as a ref ID).">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                        </span>
                    </label>
                    <input
                        type="text"
                        id="companyControlId"
                        value={selectedDetails.companyControlId || ''}
                        onChange={(e) => setSelectedDetails(prev => ({ ...prev, companyControlId: e.target.value || null }))}
                    />
                </div>
            </div>

            <div className="form-section-card">
                <div className="form-section-label">Systems</div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'mainSystem'}
                        label="Main System"
                        isRequired={isRequiredSystem(getObjectiveNum(), SystemTypes.IN_SCOPE)}
                        options={getEntitySystemList()}
                        value={selectedDetails.mainSystemId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setOpenedEntitySystemModal(SystemTypes.IN_SCOPE)) : null}
                    />
                </div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'hrSystem'}
                        label="HR System"
                        isRequired={isRequiredSystem(getObjectiveNum(), SystemTypes.HR)}
                        options={getEntitySystemList()}
                        value={selectedDetails.hrSystemId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setOpenedEntitySystemModal(SystemTypes.HR)) : null}
                    />
                </div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'ticketSystem'}
                        label="Ticket System"
                        isRequired={isRequiredSystem(getObjectiveNum(), SystemTypes.TICKET)}
                        options={getEntitySystemList()}
                        value={selectedDetails.ticketSystemId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setOpenedEntitySystemModal(SystemTypes.TICKET)) : null}
                    />
                </div>
                <ExistingControlSection
                    existingControl={existingControl}
                    onExistingControlOverride={onExistingControlOverride}
                />
                {(() => {
                    const objNum = getObjectiveNum()
                    const missing: string[] = []
                    if (isRequiredSystem(objNum, SystemTypes.IN_SCOPE) && !selectedDetails.mainSystemId) missing.push('Main System')
                    if (isRequiredSystem(objNum, SystemTypes.HR) && !selectedDetails.hrSystemId) missing.push('HR System')
                    if (isRequiredSystem(objNum, SystemTypes.TICKET) && !selectedDetails.ticketSystemId) missing.push('Ticket System')
                    if (missing.length === 0 || !objNum) return null
                    return (
                        <div className="partial-warning">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>Partial setup — missing required system{missing.length > 1 ? 's' : ''}: <strong>{missing.join(', ')}</strong></span>
                        </div>
                    )
                })()}
            </div>

            {getSelectedSystemTypes().length >= 2 && (
                <div className="form-section-card">
                    <div className="form-section-label">Consolidation Config</div>
                    <ConsolidationConfigBuilder
                        value={selectedDetails.consolidationJson || '[]'}
                        onChange={onConsolidationChange}
                        availableSystemTypes={getSelectedSystemTypes()}
                    />
                </div>
            )}

            <div className="form-section-card">
                <div className="form-section-label">Audit</div>
                <div className="field-with-add">
                    <FromDropdown
                        keyName={'audit'}
                        label="Audit Year"
                        options={getAuditYearList()}
                        value={selectedDetails.auditId || ''}
                        onSelectValue={onChangeFormValue}
                        onAddBtn={getSelectedEntity() ? (() => setIsAddAuditYearModalOpen(true)) : null}
                    />
                </div>
                <div className="field">
                    <FromDropdown
                        keyName={'controlOwner'}
                        label="Control Owner"
                        options={getUsersList()}
                        value={selectedDetails.controlOwnerId || ''}
                        onSelectValue={onChangeFormValue}
                    />
                </div>
            </div>

            <p className="form-info">New audit controls are created as active by default. You can change this later.</p>

            <div className="form-actions">
                {!isAddingAuditControl && <button
                    className="main"
                    onClick={onAddAuditControl}
                    disabled={(existingControl.status !== 'fetched') || isAddingAuditControl}>
                    Create
                </button>}
                {isAddingAuditControl && <span className="spinner"><Spinner isSmall={true} /></span>}
            </div>
        </section>
    }

    return <>
        <section ref={modalRef} className="audit-control-form-modal">
            <header onMouseDown={onHeaderMouseDown}>
                <h2>{cloneFrom ? `Clone Audit Control #${cloneFrom.sourceAcId}` : 'Create Audit Control'}</h2>
                <button className="close-btn" onClick={onClose}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            {renderBody()}
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />

        {isAddEntityModalOpen && <AddEntityModal
            onAddEntity={onAddEntity}
            onCloseModal={() => setIsAddEntityModalOpen(false)}
            triggerMissingRequiredFields={triggerMissingRequiredFields}
            triggerErrorMsg={triggerErrorMsg}
        />}

        {openedEntitySystemModal && <AddEntitySystemModal
            entityName={(getSelectedEntity() || {}).name || ''}
            systemType={openedEntitySystemModal}
            onAddEntitySystem={onAddEntitySystem}
            onCloseModal={() => setOpenedEntitySystemModal(null)}
            triggerMissingRequiredFields={triggerMissingRequiredFields}
            triggerErrorMsg={triggerErrorMsg}
        />}

        {isAddAuditYearModalOpen && <AddAuditYearModal
            entityName={(getSelectedEntity() || {}).name || ''}
            existingYears={(getSelectedEntity()?.audit || []).map((a: any) => a.year)}
            onAddAuditYear={onAddAuditYear}
            onCloseModal={() => setIsAddAuditYearModalOpen(false)}
            triggerMissingRequiredFields={triggerMissingRequiredFields}
            triggerErrorMsg={triggerErrorMsg}
        />}
    </>
}
